// AI-882 — the single rAF loop that drives every Tarot Card Stage card.
//
// Why one loop instead of CSS: a declarative CSS animation can't resume from an
// arbitrary interruption point, so the legacy shuffle→pick→fly→jiggle→flip
// seams SNAP (and unmount/remount, flashing a blank frame). Here, ONE loop owns
// every card's transform. Each frame it asks the component for that card's
// target (a "scene") and eases the card's LIVE value toward it. Because the live
// value is the start of every new tween, all phase boundaries are continuous by
// construction.
//
// The component computes scenes (it knows phase + interaction state); this hook
// only interpolates and writes to the DOM. Scenes are read through a ref so the
// loop never restarts on a React re-render.
import { useEffect, useRef, RefObject } from 'react'

export interface CardScene {
  dx: number
  dy: number
  rotateZ: number
  /** 3D flip angle written to the card's flipper element (0 until jiggle/flip). */
  rotateY: number
  opacity: number
  /** Size scale about the card's bottom-center. Defaults to 1; used by the
   *  flip→land motion to shrink the card onto a smaller landing slot. */
  scaleX?: number
  scaleY?: number
  /** Time to reach this target from the card's current value. 0 = snap (e.g.
   *  a per-frame continuous motion like the shuffle riffle / jiggle / flip). */
  durationMs: number
  easing: (t: number) => number
}

interface CardValue {
  dx: number
  dy: number
  rotateZ: number
  rotateY: number
  opacity: number
  scaleX: number
  scaleY: number
}

interface CardTween {
  from: CardValue
  to: CardValue
  startMs: number | null
  durationMs: number
  easing: (t: number) => number
}

export interface UseTarotStageAnimationOptions {
  /** Run the loop while true; cancel + reset when false. */
  active: boolean
  numCards: number
  /** Card button elements (the loop writes translate+rotateZ+opacity). */
  cardEls: RefObject<(HTMLElement | null)[]>
  /** Optional flipper elements (the loop writes rotateY). */
  flipperEls?: RefObject<(HTMLElement | null)[]>
  /** Per-card scene provider, read each frame so the loop survives re-renders. */
  sceneRef: RefObject<((i: number, nowMs: number) => CardScene) | null>
}

const STACKED: CardValue = {
  dx: 0, dy: 0, rotateZ: 0, rotateY: 0, opacity: 1, scaleX: 1, scaleY: 1,
}

const EPSILON = 0.001

const targetDiffers = (tween: CardTween | undefined, scene: CardScene): boolean => {
  if (!tween) return true
  const { to } = tween
  return (
    Math.abs(to.dx - scene.dx) > EPSILON
    || Math.abs(to.dy - scene.dy) > EPSILON
    || Math.abs(to.rotateZ - scene.rotateZ) > EPSILON
    || Math.abs(to.rotateY - scene.rotateY) > EPSILON
    || Math.abs(to.opacity - scene.opacity) > EPSILON
    || Math.abs(to.scaleX - (scene.scaleX ?? 1)) > EPSILON
    || Math.abs(to.scaleY - (scene.scaleY ?? 1)) > EPSILON
  )
}

const lerpValue = (from: CardValue, to: CardValue, t: number): CardValue => ({
  dx: from.dx + (to.dx - from.dx) * t,
  dy: from.dy + (to.dy - from.dy) * t,
  rotateZ: from.rotateZ + (to.rotateZ - from.rotateZ) * t,
  rotateY: from.rotateY + (to.rotateY - from.rotateY) * t,
  opacity: from.opacity + (to.opacity - from.opacity) * t,
  scaleX: from.scaleX + (to.scaleX - from.scaleX) * t,
  scaleY: from.scaleY + (to.scaleY - from.scaleY) * t,
})

export const useTarotStageAnimation = ({
  active,
  numCards,
  cardEls,
  flipperEls,
  sceneRef,
}: UseTarotStageAnimationOptions): void => {
  // Live per-card values + tweens. Persist across frames so each new tween
  // starts from wherever the card currently is (continuity).
  const valuesRef = useRef<CardValue[]>([])
  const tweensRef = useRef<(CardTween | undefined)[]>([])
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!active) return undefined

    // Seed live values for any card that doesn't have one yet (preserve existing
    // values so an active→active numCards change doesn't snap mid-motion).
    const values = valuesRef.current
    const tweens = tweensRef.current
    for (let i = 0; i < numCards; i += 1) {
      if (!values[i]) values[i] = { ...STACKED }
    }

    const frame = (nowMs: number) => {
      const getScene = sceneRef.current
      const cards = cardEls.current
      const flippers = flipperEls?.current
      if (getScene && cards) {
        for (let i = 0; i < numCards; i += 1) {
          const scene = getScene(i, nowMs)
          let tween = tweens[i]

          if (targetDiffers(tween, scene)) {
            tween = {
              from: { ...values[i] },
              to: {
                dx: scene.dx,
                dy: scene.dy,
                rotateZ: scene.rotateZ,
                rotateY: scene.rotateY,
                opacity: scene.opacity,
                scaleX: scene.scaleX ?? 1,
                scaleY: scene.scaleY ?? 1,
              },
              startMs: nowMs,
              durationMs: scene.durationMs,
              easing: scene.easing,
            }
            tweens[i] = tween
          }

          // Advance the active tween.
          const t = tween.durationMs > 0 && tween.startMs !== null
            ? Math.min((nowMs - tween.startMs) / tween.durationMs, 1)
            : 1
          values[i] = lerpValue(tween.from, tween.to, tween.easing(t))

          const v = values[i]
          const el = cards[i]
          if (el) {
            el.style.transform = `translateX(${v.dx}px) translateY(${v.dy}px) rotate(${v.rotateZ}deg) scale(${v.scaleX}, ${v.scaleY})`
            el.style.opacity = String(v.opacity)
          }
          const fl = flippers?.[i]
          if (fl) {
            fl.style.transform = `rotateY(${v.rotateY}deg)`
          }
        }
      }
      rafRef.current = requestAnimationFrame(frame)
    }

    rafRef.current = requestAnimationFrame(frame)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Clear tweens so the next activation re-tweens from the seeded values.
      tweensRef.current = []
    }
  }, [active, numCards, cardEls, flipperEls, sceneRef])
}
